import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被删除。" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '久久在线',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiuonline.com/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://jiujiuonline.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 久久在线`}
        description={article.excerpt}
        keywords={`久久在线,${article.category},${article.tags.join(',')}`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.category, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="px-4 md:px-6 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万 阅读
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-foreground-900 text-xs leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1.5">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}